import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { ChevronDown, History, Search, X } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton } from "@/components/ui";
import { api, canManage, type AuditArea, type AuditEntry, type AuditQuery } from "@/lib/api";
import { intlLocale, t, msg } from "@/lib/i18n";

import { serverText } from "@/lib/server-text";
const AREAS: { key: AuditArea | null; label: string }[] = [
  { key: null, label: msg("Alles") },
  { key: "orders", label: msg("Aufträge") },
  { key: "products", label: msg("Artikel") },
  { key: "inbounds", label: msg("Wareneingänge") },
  { key: "settings", label: msg("Einstellungen") },
  { key: "team", label: msg("Team") },
  { key: "security", label: msg("Sicherheit") },
];
const ACTOR_LABEL: Record<string, string> = { system: "Logiful Connect", backend: "Lager", channel: "Shop" };
const entityLink = (e: AuditEntry["entity"]) =>
  e.type === "order" ? `/orders?open=${e.id}` : e.type === "product" ? `/products?open=${e.id}` : e.type === "inbound" && e.label ? `/inbounds?q=${encodeURIComponent(e.label)}` : e.type === "channel" ? `/channels/${e.id}` : null;
const fmtValue = (v: unknown) => (v == null || v === "" ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v));
const dayLabel = (iso: string) => {
  const d = new Date(iso);
  const today = new Date();
  const y = new Date(Date.now() - 86_400_000);
  if (d.toDateString() === today.toDateString()) return "Heute";
  if (d.toDateString() === y.toDateString()) return "Gestern";
  return d.toLocaleDateString(intlLocale(), { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
};

/** Wer hat wann was geändert – für Administratoren */
export function AuditPage() {
  const { tenantId, role } = useTenant();
  const [actor, setActor] = useState<NonNullable<AuditQuery["actor"]>>("people");
  const [userId, setUserId] = useState<string>("");
  const [area, setArea] = useState<AuditArea | null>(null);
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setTerm(search.trim()), 250);
    return () => clearTimeout(id);
  }, [search]);
  const team = useQuery({ queryKey: ["team-security", tenantId], queryFn: () => api.security.team(tenantId), enabled: canManage(role) });
  const base: AuditQuery = { actor, ...(userId ? { userId } : {}), ...(area ? { area } : {}), ...(term ? { search: term } : {}) };
  const q = useInfiniteQuery({
    queryKey: ["audit", tenantId, base],
    queryFn: ({ pageParam }) => api.security.audit(tenantId, { ...base, ...(pageParam ? { before: pageParam } : {}) }),
    initialPageParam: "",
    getNextPageParam: (last) => last.next ?? undefined,
    placeholderData: keepPreviousData,
    enabled: canManage(role),
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const [open, setOpen] = useState<string | null>(null);

  if (!canManage(role)) {
    return (
      <div className="pb-16">
        <PageHeader title={t("Änderungsprotokoll")} />
        <EmptyState icon={<History className="size-5" />} title={t("Nur für Administratoren")}>{t("Das Änderungsprotokoll sehen Administratoren eures Teams.")}</EmptyState>
      </div>
    );
  }

  return (
    <div className="pb-16">
      <PageHeader title={t("Änderungsprotokoll")} description={t("Wer hat wann was geändert – Aufträge, Artikel, Einstellungen, Team und Sicherheit.")} />
      <div className="px-4 md:px-8 space-y-3 max-w-5xl">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Suchen: Auftragsnr., SKU, Person oder Text …")} leading={<Search className="size-4" />} suffix={search ? <button onClick={() => setSearch("")} aria-label={t("Suche leeren")}><X className="size-3.5" /></button> : undefined} className="h-11 text-[14px]" />
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex flex-wrap p-0.5 rounded-lg bg-surface-2 border border-border">
            {AREAS.map((a) => (
              <button key={a.label} onClick={() => setArea(a.key)} aria-pressed={area === a.key} className={cx("h-7 px-3 rounded-md text-[12.5px] font-medium", area === a.key ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}>{t(a.label)}</button>
            ))}
          </div>
          <select aria-label={t("Person")} value={userId} onChange={(e) => { setUserId(e.target.value); if (e.target.value) setActor("people"); }} className={cx("h-8 rounded-lg border border-border bg-surface px-2.5 text-[12.5px]", userId && "border-ink")}>
            <option value="">{t("Alle Personen")}</option>
            {team.data?.members.map((m) => <option key={m.userId} value={m.userId}>{m.name ?? m.email}</option>)}
          </select>
          <select aria-label={t("Wer")} value={actor} onChange={(e) => { setActor(e.target.value as typeof actor); if (e.target.value !== "people") setUserId(""); }} className="h-8 rounded-lg border border-border bg-surface px-2.5 text-[12.5px]">
            <option value="people">{t("Nur Personen")}</option>
            <option value="all">{t("Personen und automatisch")}</option>
            <option value="system">{t("Nur automatisch (Lager, Shop, System)")}</option>
          </select>
        </div>

        <Card className="overflow-hidden">
          {q.isLoading && <div className="p-4 space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>}
          {!q.isLoading && items.length === 0 && <EmptyState icon={<History className="size-5" />} title={t("Keine Einträge")}>{t("Für diese Auswahl gibt es keine Änderungen.")}</EmptyState>}
          <ul>
            {items.map((e, i) => {
              const newDay = i === 0 || dayLabel(items[i - 1]!.at) !== dayLabel(e.at);
              const who = e.actor.type === "user" ? (e.actor.name ?? e.actor.email ?? "Unbekannt") : (ACTOR_LABEL[e.actor.type] ?? e.actor.type);
              const link = entityLink(e.entity);
              const hasChanges = !!e.changes && Object.keys(e.changes).length > 0;
              return (
                <Fragment key={e.id}>
                  {newDay && <li className="px-4 py-1.5 bg-surface-2/60 border-y border-border text-[11.5px] font-semibold text-fg-muted uppercase tracking-wide first:border-t-0">{dayLabel(e.at)}</li>}
                  <li className="border-b border-border last:border-0">
                    <div className="px-4 py-2.5 flex items-start gap-3 text-[13px]">
                      <span className="w-11 shrink-0 tabular-nums text-fg-subtle text-[12px] pt-px">{new Date(e.at).toLocaleTimeString(intlLocale(), { hour: "2-digit", minute: "2-digit" })}</span>
                      <span className={cx("size-6 shrink-0 rounded-full grid place-items-center text-[10.5px] font-bold", e.actor.type === "user" ? "bg-ink text-ink-fg" : "bg-surface-2 text-fg-muted border border-border")}>{who.slice(0, 1).toUpperCase()}</span>
                      <div className="min-w-0 flex-1">
                        <p>
                          <span className="font-medium">{who}</span>
                          {e.actor.staff && <Badge className="ml-1.5" tone="coral">{t("Logiful")}</Badge>}
                          {link && e.entity.label ? <> · <Link to={link} className="font-mono text-[12.5px] hover:underline">{t(e.entity.label)}</Link></> : null}
                        </p>
                        <p className="text-fg-muted">{serverText(e.message ?? e.action)}</p>
                        {hasChanges && open === e.id && (
                          <table className="mt-2 text-[12px]">
                            <tbody>
                              {Object.entries(e.changes!).map(([k, v]) => (
                                <tr key={k}><td className="pr-3 py-0.5 text-fg-subtle align-top">{k}</td><td className="py-0.5"><span className="line-through text-fg-subtle">{fmtValue(v.from)}</span> → <span className="font-medium">{fmtValue(v.to)}</span></td></tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                      {hasChanges && (
                        <button onClick={() => setOpen(open === e.id ? null : e.id)} className="shrink-0 inline-flex items-center gap-1 text-[12px] text-fg-muted hover:text-fg" aria-expanded={open === e.id}>
                          {t("Details")} <ChevronDown className={cx("size-3.5 transition-transform", open === e.id && "rotate-180")} />
                        </button>
                      )}
                    </div>
                  </li>
                </Fragment>
              );
            })}
          </ul>
          {q.hasNextPage && (
            <div className="p-3 border-t border-border text-center"><Button size="sm" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>{t("Ältere laden")}</Button></div>
          )}
        </Card>
      </div>
    </div>
  );
}
